import React, { useCallback, useMemo } from "react";
import _groupBy from "lodash/groupBy";
import _map from "lodash/map";
import {
    GridColDef,
    GridValueFormatterParams,
    GridValueGetterParams,
} from "@mui/x-data-grid-premium";

import { Base } from "../../../framework/base";
import { VehicleListItemDto } from "../../../models/transport/vehicle";
import { EmployeeListItemDto } from "../../../models/employee/employee";
import { Translations } from "../../../models/translations";
import { useTranslation } from "react-i18next";
import { WorkTimeType } from "../../../models/workShitTimeSlot/workTimeType";
import {
    GroupedTreeDataGrid,
    GroupedDataGridRow,
} from "../../framework/groupedDataGrid/groupedTreeDataGrid";
import { ICostCenterItem } from "../../../models/costCenter/costCenterItem";
import { WorkTimeReportGridRow } from "../../../models/reporting/workTimeReport";
import { ICustomerItem } from "../../../models/customer/customerItem";
import { WorkTimeDetailsTable } from "./workTimeDetailsTable";
import { SalaryRowTypeItem } from "../../../models/salary/salaryRowTypeItem";

export interface GridRow extends GroupedDataGridRow {
    rows: WorkTimeReportGridRow[];
    employees: EmployeeListItemDto[];
    vehicles: VehicleListItemDto[];
    costCenters: ICostCenterItem[];
    workTimeTypes: WorkTimeType[];
    customers: ICustomerItem[];
    salaryRowTypes: SalaryRowTypeItem[];
}

interface EmployeesVehicleGridProps {
    vehicles: VehicleListItemDto[];
    employees: EmployeeListItemDto[];
    salaryRowTypes: SalaryRowTypeItem[];
    costCenters: ICostCenterItem[];
    customers: ICustomerItem[];
    workTimeTypes: WorkTimeType[];
    workTimeReportData: WorkTimeReportGridRow[];
}

export const EmployeesVehicleGrid = ({
    vehicles,
    employees,
    salaryRowTypes,
    costCenters,
    customers,
    workTimeTypes,
    workTimeReportData,
}: EmployeesVehicleGridProps) => {
    const { t } = useTranslation();

    // Group data by date
    const groupedData = _groupBy(
        workTimeReportData,
        (w) => `${w.employeeId}_${w.dateStr}`
    );

    // Create grid rows, calculate totals for each group
    const gridRows: GridRow[] = _map(groupedData, (value, key) => {
        const workSlot = value[0];
        const date = workSlot.dateStr;
        return {
            id: key,
            hierarchy: [workSlot.employeeId, date],
            rows: value.sort((a, b) =>
                a?.startTime?.localeCompare(b?.startTime)
            ),
            employees,
            vehicles: vehicles,
            costCenters: costCenters,
            customers,
            workTimeTypes: workTimeTypes,
            date,
            salaryRowTypes,
            totals: value.reduce((acc, curr) => {
                acc[curr.vehicleId] =
                    (acc[curr.vehicleId] ?? 0) +
                    curr.duration;

                acc["v_total"] =
                    (acc["v_total"] ?? 0) + curr.duration;

                return acc;
            }, {}),
        };
    }).sort((a, b) => Base.stringCompare(a.date, b.date));

    // Group vehicles by register number
    const groupedVehicles = vehicles?.reduce((acc, vehicle) => {
        if (!acc[vehicle.registerNumber]) {
            acc[vehicle.registerNumber] = [];
        }
        acc[vehicle.registerNumber].push(vehicle);
        return acc;
    }, {} as Record<string, typeof vehicles>);

    const gridDef: GridColDef<GridRow>[] = useMemo(
        () => [
            {
                field: "v_total",
                headerName: Translations.Total,
                description: Translations.Total,
                groupable: false,
                type: "number",
                minWidth: 100,
                flex: 1,
                valueGetter(params) {
                    return (params.row?.totals?.v_total ?? 0) / 60;
                },
                valueFormatter: (params: GridValueFormatterParams<number>) => {
                    const value = params.value;
                    if (!value) return "-";
                    return value.toLocaleFixed(2, "fi-Fi");
                },
            },
            ...Object.entries(groupedVehicles).map(
                ([registerNumber, vehicles]) => ({
                    // if multiple vehicleIds has same register number, use id of the last vehicle
                    // otherwise GroupedDataGrid will hide the column because it can't find values from row.totals[vehicleId]
                    field:
                        vehicles?.length > 1
                            ? vehicles[vehicles.length - 1]?.id
                            : vehicles[0]?.id,
                    headerName: registerNumber,
                    description: `${registerNumber} - ${vehicles
                        .map((v) => v.brand)
                        .join(", ")}`,
                    groupable: false,
                    type: "number",
                    minWidth: 100,
                    flex: 1,
                    valueGetter(params: GridValueGetterParams<GridRow>) {
                        return (
                            vehicles.reduce(
                                (sum, v) =>
                                    sum + (params.row?.totals?.[v.id] ?? 0),
                                0
                            ) / 60
                        );
                    },
                    valueFormatter: (
                        params: GridValueFormatterParams<number>
                    ) => {
                        const value = params.value;
                        if (!value) return "-";
                        return value.toLocaleFixed(2, "fi-Fi");
                    },
                })
            ),
        ],
        [vehicles]
    );

    const pinnedColumns = useMemo(() => ["v_total"], []);

    const getEmployeeLabel = useCallback(
        (employeeId: string) =>
            employees?.find((e) => e.id === employeeId)?.fullName ?? "",
        [employees]
    );

    return (
        <GroupedTreeDataGrid
            noRowsText={t("employee.noRows")}
            detailsComponent={WorkTimeDetailsTable}
            columns={gridDef}
            rows={gridRows}
            groupingColDef={{
                headerName: t("employee.employee"),
                valueFormatter: (data) => {
                    // for exports
                    const value: string = data?.value ?? Translations.Total;
                    if (value.match(/\w{8}-\w{4}-\w{4}-\w{4}-\w{12}/)) {
                        // val is employeeId
                        return getEmployeeLabel(value);
                    } else {
                        // val is date
                        return value;
                    }
                },
            }}
            getGroupLabel={getEmployeeLabel}
            persistStateKey="EmployeesVehicleGridState"
            pinnedColumns={pinnedColumns}
            showLeafLabelAsDate={true}
        />
    );
};
